import React from 'react';
import { FormattedMessage } from 'react-intl-next';

import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { QuickInsertPlugin } from '@atlaskit/editor-plugin-quick-insert';
import Button from '@atlaskit/button/new';
import ButtonGroup from '@atlaskit/button/button-group';
import { CloseButton, ModalBody, ModalFooter, ModalHeader } from '@atlaskit/modal-dialog';
import { Stack, Text } from '@atlaskit/primitives/compiled';

import type { DialogsContainerProps } from '@confluence/dialogs/entry-points/DialogsStateContainer.ts';
import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs.ts';

import type { AnalyticsOrigin } from '../constants/snippetAnalytics.ts';
import {
	ANALYTICS_ORIGIN,
	ANALYTICS_UI_SUBJECT_ID,
	MODAL_OUTCOME,
} from '../constants/snippetAnalytics.ts';
import { useSnippetAnalytics } from '../hooks/analytics/useSnippetAnalytics.ts';
import type { Snippet } from '../types/snippet.ts';

import { EditSnippetModal } from './EditSnippetModal.tsx';
import { messages } from './PrivateTemplateCopyLinkModal.i18n.ts';

export type PrivateTemplateCopyLinkModalProps = DialogsContainerProps & {
	snippet: Snippet;
	editorAPI?: PublicPluginAPI<[QuickInsertPlugin]> | null;
	contentId: string;
	spaceId: string;
	spaceKey: string;
	analyticsOrigin?: AnalyticsOrigin;
};

export const PrivateTemplateCopyLinkModal = ({
	snippet,
	onClose,
	onCloseComplete,
	editorAPI,
	contentId,
	spaceId,
	spaceKey,
	analyticsOrigin = ANALYTICS_ORIGIN.SNIPPET_PANEL,
}: PrivateTemplateCopyLinkModalProps) => {
	const { Modal } = useChoreographerAssets();
	const { showModal } = useDialogs();
	const { fireModalClosedAnalytic } = useSnippetAnalytics(analyticsOrigin);

	const handleCancel = () => {
		fireModalClosedAnalytic(ANALYTICS_UI_SUBJECT_ID.COPY_SNIPPET_LINK, {
			snippetId: snippet.snippetId,
			outcome: MODAL_OUTCOME.CANCELLED,
		});
		onClose();
	};

	const handleEditTemplateSettings = () => {
		fireModalClosedAnalytic(ANALYTICS_UI_SUBJECT_ID.COPY_SNIPPET_LINK, {
			snippetId: snippet.snippetId,
			outcome: MODAL_OUTCOME.CLOSED,
		});
		onClose();
		showModal(EditSnippetModal, {
			snippet,
			editorAPI,
			contentId,
			spaceId,
			spaceKey,
		});
	};

	return (
		<Modal onClose={handleCancel} onCloseComplete={onCloseComplete}>
			<ModalHeader>
				<Text weight="semibold" size="large">
					<FormattedMessage {...messages.title} />
				</Text>
				<CloseButton onClick={handleCancel} />
			</ModalHeader>
			<ModalBody>
				<Stack space="space.200">
					<Text>
						<FormattedMessage {...messages.body} />
					</Text>
				</Stack>
			</ModalBody>
			<ModalFooter>
				<ButtonGroup>
					<Button appearance="subtle" onClick={handleCancel}>
						<FormattedMessage {...messages.cancelButton} />
					</Button>
					<Button appearance="primary" onClick={handleEditTemplateSettings}>
						<FormattedMessage {...messages.editTemplateSettingsButton} />
					</Button>
				</ButtonGroup>
			</ModalFooter>
		</Modal>
	);
};
